import Nav from "../component/nav/nav";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InsightsIcon from "@mui/icons-material/Insights";
import EditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  resetPviChecklist,
  setPviInitial,
} from "../store/slices/pvi_checklist.slices";
import {
  resetChecks,
  setAFEIntial,
} from "../store/slices/afe_checkList.sllices";
import {
  resetSystemChecklist,
  setChecklistInitial,
} from "../store/slices/pvc_checklsit.slices";
import {
  resetInspectionState,
  setInspectionIntial,
  setReferenceNo,
} from "../store/slices/inspection_info.slices";
import {
  resetStringArray,
  setPVAInitial,
} from "../store/slices/pva_testReport.slices";
import { db } from "../firebase.config";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { INSPECTOR_LIST } from "../incpection.com/data/inspector_data";

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [projectList, setProjectList] = useState([]);
  const [paginator, setPaginator] = useState({ page: 0, perPage: 10 });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Fetch projects from Firestore
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "projects"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projects = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProjectList(projects);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ✅ Generate next reference number
  const generateNextRefNo = (lastRef = "A000") => {
    let prefix = lastRef[0];
    let number = parseInt(lastRef.slice(1));

    if (number < 999) number++;
    else {
      prefix = String.fromCharCode(prefix.charCodeAt(0) + 1);
      number = 0;
    }

    return `${prefix}${number.toString().padStart(3, "0")}`;
  };

  // ✅ Create new project
  const createNewProject = () => {
    dispatch(resetPviChecklist());
    dispatch(resetSystemChecklist());
    dispatch(resetInspectionState());
    dispatch(resetChecks());
    dispatch(resetStringArray());

    const lastRef = localStorage.getItem("lastRef") || "A000";
    const nextRef = generateNextRefNo(lastRef);
    dispatch(setReferenceNo(nextRef));
    localStorage.removeItem("ProjectID");

    navigate("/createProject");
  };

  // ✅ DataGrid columns
  const columns = [
    {
      field: "clientName",
      headerName: "Client Name",
      flex: 1,
      renderCell: ({ row }) => {
        return row?.formData?.clientName || "--";
      },
      sortable: true,
    },
    {
      field: "manualRef",
      headerName: "Project Refrence",
      flex: 1,
      renderCell: ({ row }) => {
        return row?.formData?.manualRef || "--";
      },
      sortable: true,
    },
    {
      field: "installationAddress",
      headerName: "Address",
      flex: 1,
      renderCell: ({ row }) => {
        return row?.formData?.installationAddress || "--";
      },
    },
    {
      field: "name",
      headerName: "Inspector name",
      flex: 1,
      renderCell: ({ row }) => {
        const inspector = INSPECTOR_LIST.find(
          (ins) => ins.id === row?.selectedInspectorId
        );
        return inspector?.name || "--";
      },
    },
    {
      field: "power",
      headerName: "Power (kW)",
      flex: 1,
      renderCell: ({ row }) => {
        return row?.formData?.power || "--";
      },
      sortable: true,
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      renderCell: ({ row }) => {
        const date = row?.updatedAt?.toDate
          ? row.updatedAt.toDate()
          : new Date(row.updatedAt);
        return date ? date.toISOString().split("T")[0] : "-";
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="preview" placement="top">
              <InsightsIcon style={{ color: "green", width: 24, height: 24 }} />
            </Tooltip>
          }
          label="Detail"
          onClick={() => handlePreview(row, "PREVIEW")}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit" placement="top">
              <EditIcon style={{ color: "#27326A", width: 24, height: 24 }} />
            </Tooltip>
          }
          label="Edit"
          onClick={() => handlePreview(row, "EDIT")}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete" placement="top">
              <DeleteIcon style={{ color: "red", width: 24, height: 24 }} />
            </Tooltip>
          }
          label="Delete"
          onClick={() => handleDelete(row?.id)}
        />,
      ],
    },
  ];

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?"))
      return;

    try {
      // Delete the project document from Firestore
      await deleteDoc(doc(db, "projects", id));

      // Optionally, remove it from the local state instantly
      setProjectList((prev) => prev.filter((proj) => proj.id !== id));

      console.log("✅ Project deleted:", id);
    } catch (error) {
      console.error("❌ Error deleting project:", error);
      alert("Failed to delete project. Please try again.");
    }
  };

  const handlePreview = (row, value) => {
    localStorage.setItem("ProjectID", row?.id);
    dispatch(setAFEIntial(row?.arrayFrameEarthing));
    dispatch(setPviInitial(row?.pvInstallation));
    dispatch(setChecklistInitial(row?.checklistData));
    dispatch(
      setPVAInitial({
        invertersList: row?.invertersList,
        selectedPanel: row?.selectedPanel,
      })
    );
    dispatch(
      setInspectionIntial({
        formData: row?.formData,
        selectedInspectorId: row?.selectedInspectorId,
        referenceNo: row?.referenceNo,
      })
    );

    if (value === "EDIT") {
      navigate("/createProject");
    } else if (value === "PREVIEW") {
      navigate("/preview");
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredProjects = projectList.filter((project) =>
    project?.formData?.clientName
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* === Top Navigation === */}
      <Box
        sx={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}
      >
        <Nav />
      </Box>

      <Box className="d_content">
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            color: "rgb(39, 50, 106)",
            fontStyle: "italic",
            fontFamily: "sans-serif",
          }}
        >
          PV INSPECTED PROJECTS
        </Typography>

        {/* === Toolbar === */}
        <Box
          className="tb_dashboard"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "1%",
            marginTop: "1%",
            gap: "20px",
            alignItems: "end",
          }}
        >
          <TextField
            label="Search"
            className="search"
            margin="dense"
            variant="outlined"
            placeholder="search by client name"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: "30ch" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            style={{
              backgroundColor: "#27326A",
              width: "155px",
              height: "40px",
              textTransform: "none",
              marginBottom: "5px",
            }}
            onClick={createNewProject}
          >
            <AddCircleOutlineIcon
              sx={{ marginRight: "10px", width: 18, height: 18 }}
            />
            Add New Project
          </Button>
        </Box>

        {/* === Project Table === */}
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DataGrid
            loading={loading}
            sx={{
              borderRadius: 3,
              border: 0,
              width: "100%",
              minWidth: "720px",
              outline: "none",
            }}
            disableColumnMenu
            disableSelectionOnClick
            getRowId={(row) => row.id}
            columns={columns}
            rows={filteredProjects}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: paginator.perPage },
              },
            }}
            paginationMode="client"
            // rowCount={projectList.length}
            onPaginationModelChange={(newPage) => {
              setPaginator({
                page: newPage?.page,
                perPage: newPage?.pageSize,
              });
            }}
            pageSizeOptions={[10]}
          />
        </Box>
      </Box>
    </Box>
  );
}
